// apps/client/src/components/reports/VarianceKpiStrip.tsx
// Five stat cards. The first three are FULL-BOOK headline (every settled loan
// in the selected period, respects dimension filters + RBAC, ignores mode
// toggles) — so "Total Settled" reconciles to the main dashboard's "Loans
// Settled". The last two are variance-subset (under-settled loans only) and
// surface the leakage story. The Decreased-only / Hide-construction-likely
// toggles only affect those last two cards + the tables below.
//
// Why this split (post Decru 2026-05-26): when all five cards were scoped to
// the variance subset, "Total Settled $26.64M" read as a wrong company-wide
// total against the dashboard's $68.64M — a credibility failure. The column
// was correct; the scope wasn't.

import React from 'react';
import { Banknote, CheckCircle2, Wallet, AlertTriangle, ListChecks } from 'lucide-react';
import StatCard from '../common/StatCard';
import { formatCurrencyShorthand } from '../../utils/formatters';
import type { VarianceKpis, SubsequentUpfrontMode } from '../../hooks/reports/useApprovedVsSettledVariance';

interface VarianceKpiStripProps {
  kpis: VarianceKpis;
  loading?: boolean;
  periodLabel: string;
  /** Tenant treatment of top-up (subsequent) upfronts; 'off' hides the note. */
  subsequentUpfrontMode?: SubsequentUpfrontMode;
}

export const VarianceKpiStrip: React.FC<VarianceKpiStripProps> = ({
  kpis, loading, periodLabel, subsequentUpfrontMode = 'off',
}) => {
  const settledPctOfApproved = kpis.fullBookApprovedCents > 0
    ? (kpis.fullBookSettledCents / kpis.fullBookApprovedCents) * 100
    : 0;

  const decreasePctLabel = `${(kpis.upfrontDecreasePct * 100).toFixed(1)}% uplift`;

  // Tooltip text shared by the two leakage cards — written once, applied to
  // both via StatCard's subtitle so users understand why those numbers are
  // smaller than the headline.
  const leakageScopeNote = 'scoped to under-settled loans';

  const cards = [
    // ── Headline (full-book, reconciles to main dashboard) ───────────────
    {
      title: 'Total Approved',
      value: formatCurrencyShorthand(kpis.fullBookApprovedCents),
      icon: <Banknote className="w-5 h-5" />,
      gradientColor: '#3b82f6',
      subtitle: `${kpis.fullBookLoanCount.toLocaleString()} loans settled`,
    },
    {
      title: 'Total Settled',
      value: formatCurrencyShorthand(kpis.fullBookSettledCents),
      icon: <CheckCircle2 className="w-5 h-5" />,
      gradientColor: '#22c55e',
      subtitle: [
        kpis.fullBookApprovedCents > 0 ? `${settledPctOfApproved.toFixed(1)}% of approved` : '—',
        // Under enforce the top-up is already inside the total; under observe it is
        // measured but not yet added, so the note says what the total would move by.
        subsequentUpfrontMode !== 'off' && kpis.fullBookSubsequentLoanCount > 0
          ? `${subsequentUpfrontMode === 'enforce' ? 'incl.' : 'excl.'} ${formatCurrencyShorthand(kpis.fullBookSubsequentAmountCents)} top-ups on ${kpis.fullBookSubsequentLoanCount.toLocaleString()} loans`
          : '',
      ].filter(Boolean).join(' · '),
    },
    {
      title: 'Upfront Paid',
      value: formatCurrencyShorthand(kpis.fullBookUpfrontActualCents),
      icon: <Wallet className="w-5 h-5" />,
      gradientColor: '#6366f1',
      subtitle: subsequentUpfrontMode !== 'off' && kpis.fullBookSubsequentUpfrontCents > 0
        ? `incl. ${formatCurrencyShorthand(kpis.fullBookSubsequentUpfrontCents)} subsequent upfronts`
        : 'Actual commission received',
    },
    // ── Leakage (variance subset — under-settled loans only) ─────────────
    {
      title: 'Commission At Risk',
      value: formatCurrencyShorthand(kpis.upfrontDecreaseCents),
      icon: <AlertTriangle className="w-5 h-5" />,
      gradientColor: '#ef4444',
      subtitle: kpis.shortfallLoans > 0
        ? `${decreasePctLabel} · ${leakageScopeNote}`
        : `No shortfall · ${leakageScopeNote}`,
    },
    {
      title: 'Short Loans',
      value: kpis.fullBookLoanCount > 0
        ? `${kpis.shortfallLoans.toLocaleString()} of ${kpis.fullBookLoanCount.toLocaleString()}`
        : kpis.shortfallLoans.toLocaleString(),
      icon: <ListChecks className="w-5 h-5" />,
      gradientColor: '#f97316',
      subtitle: `Settled below approved · ${leakageScopeNote}`,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
      {cards.map((card, i) => (
        <StatCard
          key={i}
          title={card.title}
          value={card.value}
          icon={card.icon}
          gradientColor={card.gradientColor}
          subtitle={card.subtitle}
          periodLabel={periodLabel}
          isUpdating={loading}
        />
      ))}
    </div>
  );
};

export default VarianceKpiStrip;
